/**
 * Normalizes and sanitizes a text string by removing diacritics, lowercasing, and trimming whitespace.
 *
 * This function is designed for text normalization and comparison purposes — for example,
 * standardizing user input, performing case-insensitive matching, or simplifying search tokens.
 *
 * It performs the following steps:
 * 1. **Unicode normalization (NFD)** — Decomposes combined characters into base letters + diacritic marks.
 * 2. **Diacritic stripping** — Removes all combining accent marks using a Unicode range regex (`\u0300-\u036f`).
 * 3. **Lowercasing** — Converts the string to lowercase to ensure case-insensitive comparisons.
 * 4. **Trimming** — Removes leading and trailing whitespace.
 *
 * @param {string} s - The input string to normalize. Should be a valid UTF-8 string; if already plain ASCII, this function is safe and idempotent.
 * @returns {string} A cleaned, lowercased, diacritic-free, and trimmed version of the input string.
 *
 * @example
 * normalizeText('  Résumé '); // 'resume'
 * normalizeText('Café');      // 'cafe'
 * normalizeText('Ångström');  // 'angstrom'
 * normalizeText('HELLO');     // 'hello'
 *
 * // This function is commonly used for:
 * // - Case-insensitive string comparison
 * // - Normalizing search queries
 * // - Collapsing accented characters into plain ASCII
 */
export const normalizeText = (s: string) =>
    s.normalize('NFD')                // Step 1: Decompose accented characters (e.g., "é" → "e" + "́")
     .replace(/[\u0300-\u036f]/g, '') // Step 2: Remove all diacritic marks from the decomposed form
     .toLowerCase()                   // Step 3: Convert to lowercase for uniform comparison
     .trim();                         // Step 4: Remove any leading or trailing whitespace
